import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useRoleFilter } from "../lib/roleFilter";
import { LayoutGrid, LogOut, Users, Terminal, Activity, Building2, Gauge, CalendarDays, Sun, Moon } from "lucide-react";

/**
 * OS Control Center shell — dark theme (Emergent-chat inspired).
 * - Persistent left rail (nav + system status)
 * - Top command bar with live clock + system indicator
 * - Everything is dense, high-contrast, monospace-touched
 */
export default function Shell({ children }) {
  const { user, logout, logoutEverywhere } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const nav = [
    { to: "/dashboard", label: "Command Center", icon: Gauge, testid: "nav-command-center" },
    { to: "/events", label: "Events", icon: LayoutGrid, testid: "nav-events" },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
    { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/venues", label: "Venues", icon: Building2, testid: "nav-venues" },
  ];

  const onLogout = async () => { await logout(); navigate("/"); };
  const onLogoutEverywhere = async () => {
    if (!window.confirm(
      "Sign out on every browser + device you're currently signed in on? "
      + "You'll need to sign in fresh next time."
    )) return;
    await logoutEverywhere();
    navigate("/");
  };

  const timeStr = clock.toLocaleTimeString(undefined, { hour12: false });
  const dateStr = clock.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" }).toUpperCase();

  return (
    <div className="min-h-screen flex bg-canvas text-app">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-[240px] flex-col border-r border-hair bg-surface">
        {/* Brand block — reads as a system window titlebar */}
        <div className="border-b border-hair">
          <Link to="/dashboard" className="block px-4 py-4" data-testid="brand-link">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 bg-surface-2 border border-hair rounded-md flex items-center justify-center overflow-hidden">
                <img src="/atelis-icon-192.png" alt="AtelisOS" className="w-full h-full object-contain" />
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-faint uppercase">AtelisOS v1.2</div>
            </div>
            <div className="font-display text-lg font-bold text-aurora leading-tight -tracking-[0.02em]">Control Center</div>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-0.5">
          <div className="px-2 pb-2 font-mono text-[10px] tracking-[0.2em] text-faint uppercase">Workspaces</div>
          {nav.map((n) => {
            const active = location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-all ${
                  active
                    ? "bevel-2 border-hair-emph text-app"
                    : "border-transparent text-muted hover:border-hair hover:bg-surface-2 hover:text-app"
                }`}
              >
                {active && (
                  <span className="absolute left-0 top-1 bottom-1 w-[2px] bg-aurora rounded-full" aria-hidden />
                )}
                <Icon size={15} strokeWidth={1.75} />
                <span className="text-sm font-medium">{n.label}</span>
                {active && <span className="ml-auto font-mono text-[10px] text-aurora opacity-90">ACTIVE</span>}
              </Link>
            );
          })}
        </nav>

        {/* System status card */}
        <div className="border-t border-hair px-3 py-3 space-y-2">
          <div className="flex items-center justify-between font-mono text-[10px] tracking-widest uppercase text-faint">
            <span>System</span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full live-pulse" />
              <span className="text-emerald-400">Online</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-xs text-muted tabular-nums">{dateStr} · {timeStr}</div>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              aria-pressed={theme === "light"}
              className="inline-flex items-center gap-1 px-2 h-6 rounded-full bevel-btn text-muted hover:text-app transition-colors"
            >
              {theme === "light"
                ? <Moon size={11} strokeWidth={2} data-testid="theme-icon-moon" />
                : <Sun  size={11} strokeWidth={2} data-testid="theme-icon-sun" />}
              <span className="font-mono text-[9px] tracking-[0.18em] uppercase">
                {theme === "light" ? "Light" : "Dark"}
              </span>
            </button>
          </div>
        </div>

        {/* Account */}
        <div className="border-t border-hair p-3">
          <div className="flex items-center gap-2.5">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 object-cover" style={{ borderRadius: "2px" }} />
            ) : (
              <div className="w-8 h-8 bg-surface-2 border border-hair text-app flex items-center justify-center font-mono text-xs font-bold rounded-md">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-app truncate" data-testid="user-name">{user?.name}</div>
              <div className="font-mono text-[10px] text-faint truncate">{user?.email}</div>
            </div>
            <button
              onClick={onLogoutEverywhere}
              data-testid="logout-everywhere-btn"
              className="text-faint hover:text-[#F59E0B] p-1 border border-transparent hover:border-hair font-mono text-[9px] tracking-[0.15em] uppercase"
              title="Sign out on every device"
            >
              All
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="text-faint hover:text-[#EF4444] p-1 border border-transparent hover:border-hair"
              title="Sign out"
            >
              <LogOut size={14} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-surface border-b border-hair px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-6 h-6 bg-surface-2 border border-hair rounded-md flex items-center justify-center overflow-hidden">
            <img src="/atelis-icon-192.png" alt="AtelisOS" className="w-full h-full object-contain" />
          </div>
          <span className="font-display text-sm font-bold text-app">Control Center</span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
            aria-label="Toggle theme"
            className="text-faint p-1"
          >
            {theme === "light" ? <Moon size={16} strokeWidth={1.75} /> : <Sun size={16} strokeWidth={1.75} />}
          </button>
          <button onClick={onLogout} data-testid="logout-btn-mobile" className="text-faint">
            <LogOut size={16} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Main region with top command bar */}
      <main className="flex-1 md:ml-0 pt-14 md:pt-0 min-w-0 flex flex-col">
        {/* Command bar */}
        <div className="hidden md:flex items-center justify-between border-b border-hair bg-surface px-6 py-2.5 gap-4">
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-widest uppercase text-faint shrink-0">
            <Terminal size={13} strokeWidth={1.75} />
            <span>{routeLabel(location.pathname)}</span>
          </div>
          <ShellRoleFilter />
          <div className="flex items-center gap-4 font-mono text-[11px] tabular-nums text-muted shrink-0">
            <span className="flex items-center gap-1.5 text-emerald-400">
              <Activity size={12} strokeWidth={2} />
              <span>OPS OK</span>
            </span>
            <span className="text-faint">|</span>
            <span>{timeStr}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 bg-canvas">{children}</div>
      </main>
    </div>
  );
}

function routeLabel(pathname) {
  if (pathname.startsWith("/events/")) return "/events/detail";
  if (pathname.startsWith("/clients/")) return "/clients/detail";
  if (pathname.startsWith("/dashboard")) return "/dashboard";
  if (pathname.startsWith("/events")) return "/events";
  if (pathname.startsWith("/calendar")) return "/calendar";
  if (pathname.startsWith("/clients")) return "/clients";
  if (pathname.startsWith("/venues")) return "/venues";
  return pathname;
}

/**
 * Compact role-filter chip bar rendered inline in the Shell top-nav.
 * Auto-hides when the current user has events in fewer than 2 different roles
 * (no ambiguity to disambiguate). Populated by whichever page pushes
 * `availableRoles` into the context — typically the Dashboard on load.
 */
const ROLE_LABELS = {
  owner: "Planner",
  coordinator: "Coordinator",
  vendor: "Vendor",
  client: "Client",
  member: "Member",
};

function ShellRoleFilter() {
  const { value, setValue, availableRoles } = useRoleFilter();
  if (!availableRoles || availableRoles.length < 2) return <div className="flex-1" />;
  const ordered = ["owner", "coordinator", "vendor", "client", "member"]
    .filter((r) => availableRoles.includes(r));
  return (
    <div
      className="flex-1 flex items-center justify-center gap-1.5 flex-wrap"
      data-testid="shell-role-filter"
    >
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-faint mr-1">
        Viewing:
      </span>
      <ShellChip active={value === "all"} onClick={() => setValue("all")} testid="shell-role-all">
        All
      </ShellChip>
      {ordered.map((r) => (
        <ShellChip
          key={r}
          active={value === r}
          onClick={() => setValue(r)}
          testid={`shell-role-${r}`}
        >
          As {ROLE_LABELS[r] || r}
        </ShellChip>
      ))}
    </div>
  );
}

function ShellChip({ active, onClick, testid, children }) {
  const cls = active
    ? "bg-app text-canvas border-app"
    : "bg-transparent text-muted border-hair hover:border-hair-emph hover:text-app";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center h-7 px-2.5 border rounded-sm font-mono text-[10px] tracking-[0.15em] uppercase transition-colors ${cls}`}
    >
      {children}
    </button>
  );
}
